import { ReactElement } from 'react';
import { Typography } from '@mui/material';
import { largeCurrencyFormat, smallCurrencyFormat } from 'common/formats/formats';
import { GridType, Row } from 'common/types/scalarSpreadsheet';
import DeltaCell from 'pages/Intelligence/DailyNav/components/DeltaCell';
import ReviewPriceCell from 'pages/Intelligence/DailyNav/components/ReviewPriceCell';
import {
  buildDailyNavSections,
  DAILY_NAV_COLUMN_TYPES,
  DAILY_NAV_SPREADSHEET_SECTIONS_ALIAS,
  DAILY_NAV_SPREADSHEET_TITLE_ALIAS,
  getDailyNavColumnLabel,
  getDailyNavColumnLabels,
  parseSecondaryPriceProviderCode,
} from 'pages/Intelligence/DailyNav/dailyNav.constants';
import { getExpression, getObjectValue } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { DailyNavColumn, DailyNavTableRow } from '../../useDailyNavSheet.types';

// The metric that produced the deltas, as named by the server. Every row in a
// request shares one metric, so the first breakdown that carries a name wins;
// "Value" keeps the header honest when the catalog was unavailable.
const getMetricLabel = (dailyNavRows: DailyNavTableRow[]) =>
  dailyNavRows.find(row => row.record.breakdown?.metric?.display_name)?.record.breakdown?.metric
    ?.display_name ?? 'Value';

// First-row-wins, like getMetricLabel: every row shares the firm's counts.
const getWindowSessionCounts = (dailyNavRows: DailyNavTableRow[]) => {
  const row = dailyNavRows.find(
    candidate =>
      candidate.record.breakdown?.short_window_sessions != null &&
      candidate.record.breakdown?.long_window_sessions != null
  );
  return {
    shortWindowSessions: row?.record.breakdown?.short_window_sessions,
    longWindowSessions: row?.record.breakdown?.long_window_sessions,
  };
};

// Map column index -> section band cell config (label + span at each section start).
// Sections are (re)built per render because the Secondary Data section's member
// list depends on which per-provider price columns are present this time.
const getSectionBandByColumnIndex = (columnTypeOrder: string[], metricLabel: string) => {
  const bandByIndex = new Map<number, { label: string; colSpan: number }>();
  buildDailyNavSections(columnTypeOrder).forEach(section => {
    const memberIndexes = section.columnTypes
      .map(columnType => columnTypeOrder.indexOf(columnType))
      .filter(index => index >= 0)
      .sort((a, b) => a - b);
    if (memberIndexes.length) {
      const label = section.labelFromMetric ? `${section.label} ${metricLabel}` : section.label;
      bandByIndex.set(memberIndexes[0], { label, colSpan: memberIndexes.length });
    }
  });
  return bandByIndex;
};

// The column index each section starts at, minus the leftmost one: a divider on
// the first data column would double up with the grid's own edge. Derived from
// the same band map, so the rule can never drift from where the bands actually
// begin -- which matters because the provider columns shift those boundaries.
const getSectionDividerColumnIndexes = (
  sectionBandByColumnIndex: Map<number, { label: string; colSpan: number }>
): Set<number> => {
  const startIndexes = Array.from(sectionBandByColumnIndex.keys()).sort((a, b) => a - b);
  return new Set(startIndexes.filter(index => index > 0));
};

const SectionBandLabel = ({ label }: { label: string }) => (
  <Typography
    variant="caption"
    sx={{
      color: 'text.secondary',
      display: 'block',
      fontWeight: 600,
      letterSpacing: 0.75,
      textAlign: 'center',
      textTransform: 'uppercase',
    }}>
    {label}
  </Typography>
);

interface CellParserParams {
  column: DailyNavColumn;
  columnType: string;
  colIndex: number;
  key: string;
  row: Row<object>;
  rowByAlias: Record<string, DailyNavTableRow>;
  sectionBandByColumnIndex: Map<number, { label: string; colSpan: number }>;
  sectionDividerColumnIndexes: Set<number>;
  columnLabels: Record<string, string>;
  onReviewClick: (row: DailyNavTableRow) => void;
  canReview: boolean;
  onBreakdownClick: (row: DailyNavTableRow) => void;
  onSecondaryBreakdownClick: (row: DailyNavTableRow) => void;
}

interface DailyNavCell extends Row<object> {
  key: string;
  className?: string;
  format?: object | null;
  gridType?: GridType;
  component?: ReactElement | null;
  forceComponent?: boolean;
  colSpan?: number | null;
  customFormat?: { currency: { code: string } };
}

// Values are stored in each company's cap-table currency; default to USD when a row
// has no snapshot currency so the symbol never renders blank.
const DEFAULT_CURRENCY_CODE = 'USD';

const cellParser = (params: CellParserParams): DailyNavCell => {
  const {
    column,
    columnType,
    colIndex,
    key,
    row,
    rowByAlias,
    sectionBandByColumnIndex,
    sectionDividerColumnIndexes,
    columnLabels,
    onReviewClick,
    canReview,
    onBreakdownClick,
    onSecondaryBreakdownClick,
  } = params;
  const { alias: rowAlias } = getObjectValue(row);

  const columnCellValue = getObjectValue(column[String(rowAlias)]).value;
  const cell = { ...row, key, value: columnCellValue } as DailyNavCell;

  // Vertical rule at each section boundary. Applied before the per-column-type
  // branches below because several of them return early, and appended to the
  // row's own className rather than replacing it.
  if (sectionDividerColumnIndexes.has(colIndex)) {
    cell.className = [cell.className, 'division-left-only'].filter(Boolean).join(' ');
  }

  if (rowAlias === DAILY_NAV_SPREADSHEET_SECTIONS_ALIAS) {
    const band = sectionBandByColumnIndex.get(colIndex);
    cell.value = '';
    if (band) {
      cell.colSpan = band.colSpan;
      cell.component = <SectionBandLabel label={band.label} />;
      cell.forceComponent = true;
    }
    return cell;
  }

  if (rowAlias === DAILY_NAV_SPREADSHEET_TITLE_ALIAS) {
    cell.value = getDailyNavColumnLabel(columnType, columnLabels);
    return cell;
  }

  // Per-row currency: each company's monetary cells render in its own cap-table
  // currency rather than a spreadsheet-wide default. FastValueViewer resolves a
  // number cell's currency from customFormat.currency (it ignores cell.currency and
  // spreads the merged currency last), so the code must be injected there.
  const rowCurrency = rowByAlias[String(rowAlias)]?.lookingGlass?.cap_table_currency || DEFAULT_CURRENCY_CODE;
  const applyCurrency = () => {
    cell.customFormat = { currency: { code: rowCurrency } };
  };

  // Dynamic per-provider Secondary Data price column: currency-formatted like
  // PRICE_PER_SHARE, blank (not $0.00) when this row has no value for the provider.
  const secondaryPriceProviderCode = parseSecondaryPriceProviderCode(columnType);
  if (secondaryPriceProviderCode) {
    if (columnCellValue == null) {
      cell.gridType = 'string';
      cell.value = '';
    } else {
      cell.format = smallCurrencyFormat;
      cell.gridType = 'number';
      applyCurrency();
    }
    return cell;
  }

  switch (columnType) {
    case DAILY_NAV_COLUMN_TYPES.EQUITY_VALUE:
      // Companies with no Looking Glass snapshot should read as blank, not $0.
      if (columnCellValue == null) {
        cell.gridType = 'string';
        cell.value = '';
      } else {
        cell.format = largeCurrencyFormat;
        cell.gridType = 'number';
        applyCurrency();
      }
      break;

    case DAILY_NAV_COLUMN_TYPES.PRICE_PER_SHARE:
      if (columnCellValue == null) {
        cell.gridType = 'string';
        cell.value = '';
      } else {
        cell.format = smallCurrencyFormat;
        cell.gridType = 'number';
        applyCurrency();
      }
      break;

    case DAILY_NAV_COLUMN_TYPES.SECONDARY_SHORT_WINDOW_DELTA:
    case DAILY_NAV_COLUMN_TYPES.SECONDARY_LONG_WINDOW_DELTA: {
      // Clickable, opening the per-provider breakdown. Reuses DeltaCell with a
      // DIFFERENT handler: the component is presentation-only, so the dialog it
      // opens is entirely the caller's choice.
      cell.gridType = 'string';
      const dailyNavRow = rowByAlias[String(rowAlias)];
      if (dailyNavRow) {
        cell.component = (
          <DeltaCell
            row={dailyNavRow}
            delta={typeof columnCellValue === 'number' ? columnCellValue : null}
            onBreakdownClick={onSecondaryBreakdownClick}
            idSuffix={
              columnType === DAILY_NAV_COLUMN_TYPES.SECONDARY_SHORT_WINDOW_DELTA ? 'secondary-short-window' : 'secondary-long-window'
            }
          />
        );
        cell.forceComponent = true;
      }
      break;
    }

    case DAILY_NAV_COLUMN_TYPES.SHORT_WINDOW_DELTA:
    case DAILY_NAV_COLUMN_TYPES.LONG_WINDOW_DELTA:
    case DAILY_NAV_COLUMN_TYPES.SINCE_LAST_VALUATION_DELTA: {
      // Delta cells are clickable — they open the read-only breakdown dialog — so they
      // always render the DeltaCell component rather than a plain formatted value.
      cell.gridType = 'string';
      const dailyNavRow = rowByAlias[String(rowAlias)];
      if (dailyNavRow) {
        cell.component = (
          <DeltaCell
            row={dailyNavRow}
            delta={typeof columnCellValue === 'number' ? columnCellValue : null}
            onBreakdownClick={onBreakdownClick}
            idSuffix={columnType}
          />
        );
        cell.forceComponent = true;
      }
      break;
    }

    case DAILY_NAV_COLUMN_TYPES.STATUS: {
      const dailyNavRow = rowByAlias[String(rowAlias)];
      cell.gridType = 'string';
      if (dailyNavRow) {
        cell.component = <ReviewPriceCell row={dailyNavRow} onReviewClick={onReviewClick} canReview={canReview} />;
        cell.forceComponent = true;
      }
      break;
    }

    default:
      cell.gridType = 'string';
      break;
  }

  return cell;
};

interface CreateParserParams {
  columns: DailyNavColumn[];
  rowConfig: Row<object>[];
  dailyNavRows: DailyNavTableRow[];
  onReviewClick: (row: DailyNavTableRow) => void;
  columnTypeOrder: string[];
  canReview: boolean;
  onBreakdownClick: (row: DailyNavTableRow) => void;
  onSecondaryBreakdownClick: (row: DailyNavTableRow) => void;
}

const createParser = (params: CreateParserParams): Record<string, DailyNavCell> => {
  const {
    columns,
    rowConfig,
    dailyNavRows,
    onReviewClick,
    columnTypeOrder,
    canReview,
    onBreakdownClick,
    onSecondaryBreakdownClick,
  } = params;

  const cells: Record<string, DailyNavCell> = {};
  const alphabet = alphabetGenerator([], columns.length);
  const sectionBandByColumnIndex = getSectionBandByColumnIndex(
    columnTypeOrder,
    getMetricLabel(dailyNavRows)
  );
  const sectionDividerColumnIndexes = getSectionDividerColumnIndexes(sectionBandByColumnIndex);

  const rowByAlias: Record<string, DailyNavTableRow> = dailyNavRows.reduce(
    (accumulator, dailyNavRow) => ({ ...accumulator, [String(dailyNavRow.companyId)]: dailyNavRow }),
    {}
  );
  const { shortWindowSessions, longWindowSessions } = getWindowSessionCounts(dailyNavRows);
  const columnLabels = getDailyNavColumnLabels(shortWindowSessions, longWindowSessions);

  rowConfig.forEach((row, rowIndex) => {
    columns.forEach((column, colIndex) => {
      const rowNumber = rowIndex + 1;
      const columnLegend = alphabet[colIndex];
      const columnType = columnTypeOrder[colIndex] ?? '';
      const key = columnLegend + rowNumber;

      const cell = cellParser({
        column,
        columnType,
        colIndex,
        key,
        row,
        rowByAlias,
        sectionBandByColumnIndex,
        sectionDividerColumnIndexes,
        columnLabels,
        onReviewClick,
        canReview,
        onBreakdownClick,
        onSecondaryBreakdownClick,
      });
      cell.expr = getExpression({ expr: String(cell.expr ?? cell.value ?? ''), columnLegend });

      cells[key] = cell;
    });
  });

  return cells;
};

export default createParser;
